import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-relay';
import type { PayloadError } from 'relay-runtime';
import { useToast } from '@pv-frontend/ui-components-web';
import type { VaultDetailView, VaultOverrideView } from '../screens/vault-detail';
import {
    setVaultRingMutation,
    setFeatureOverrideMutation,
    clearFeatureOverrideMutation,
} from './vault-toggle-mutations';
import { featureErrorCode, humanizeFeatureCode } from './feature-flag-errors';

/**
 * The mutations are untyped by Relay (no generated response types are wired in),
 * so responses arrive as a loose bag keyed by the root field name.
 */
type MutationResponse = Record<string, unknown>;

type Commit = (cbs: {
    variables: Record<string, unknown>;
    onCompleted: (response: MutationResponse, errors: PayloadError[] | null) => void;
    onError: (error: Error) => void;
}) => unknown;

/** An optimistic override overlay: a state string, or `null` for a cleared flag. */
type OverrideOverlay = Record<string, string | null>;

export interface VaultTogglesController {
    /** The base vault with any in-flight optimistic ring/override changes applied. */
    vault: VaultDetailView;
    /** Toggle keys with a mutation in flight (`'ring'`, `override:<flagKey>`). */
    busyKeys: ReadonlySet<string>;
    onSetRing: (ring: string) => void;
    onSetOverride: (flagKey: string, state: string) => void;
    onClearOverride: (flagKey: string) => void;
}

/** Build a `{ title, description }` error toast, preferring a surfaced `FEATURE_*` code. */
function toggleErrorToast(source: unknown, fallbackTitle: string): { title: string; description: string } {
    const code = featureErrorCode(source);
    if (code) return { title: humanizeFeatureCode(code), description: code };
    return { title: fallbackTitle, description: 'The change was rejected. Please try again.' };
}

/** Apply the optimistic override overlay to the server's base override list. */
function mergeOverrides(
    base: readonly VaultOverrideView[],
    overlay: OverrideOverlay,
): VaultOverrideView[] {
    const merged = new Map(base.map((o) => [o.flagKey, o.state]));
    for (const [flagKey, state] of Object.entries(overlay)) {
        if (state === null) merged.delete(flagKey);
        else merged.set(flagKey, state);
    }
    return [...merged].map(([flagKey, state]) => ({ flagKey, state }));
}

/**
 * Drives the two in-context vault write actions — set ring, set/clear a per-flag
 * override — with optimistic UI: the change is applied to the merged view
 * immediately, reconciled with the server's returned state on success, or rolled
 * back on failure. Success/error toasts fire either way, surfacing `FEATURE_*`
 * codes on rejection.
 */
export function useVaultToggles(base: VaultDetailView): VaultTogglesController {
    const toast = useToast();
    const [ringOverride, setRingOverride] = useState<string | null>(null);
    const [overrides, setOverrides] = useState<OverrideOverlay>({});
    const [busyKeys, setBusyKeys] = useState<ReadonlySet<string>>(() => new Set());

    // Mirrors of the optimistic state so async commit callbacks can read the
    // value that was live *before* the change, to roll back precisely.
    const ringRef = useRef(ringOverride);
    ringRef.current = ringOverride;
    const overridesRef = useRef(overrides);
    overridesRef.current = overrides;

    const [commitRing] = useMutation(setVaultRingMutation);
    const [commitSetOverride] = useMutation(setFeatureOverrideMutation);
    const [commitClearOverride] = useMutation(clearFeatureOverrideMutation);

    const setBusy = useCallback((key: string, busy: boolean) => {
        setBusyKeys((current) => {
            const next = new Set(current);
            if (busy) next.add(key);
            else next.delete(key);
            return next;
        });
    }, []);

    const setOverride = useCallback((flagKey: string, state: string | null) => {
        setOverrides((current) => ({ ...current, [flagKey]: state }));
    }, []);

    const onSetRing = useCallback(
        (ring: string) => {
            if (ring === (ringRef.current ?? base.ring)) return;
            const previous = ringRef.current;
            setBusy('ring', true);
            setRingOverride(ring);
            commitRing({
                variables: { vaultId: base.vaultId, ring },
                onCompleted: (response, errors) => {
                    setBusy('ring', false);
                    if (errors && errors.length > 0) {
                        setRingOverride(previous);
                        const { title, description } = toggleErrorToast(errors, "Couldn't set the ring");
                        toast.error(title, description);
                        return;
                    }
                    const state = (response as MutationResponse).setVaultRing;
                    if (state && typeof state === 'object' && 'ring' in state) {
                        setRingOverride(String((state as { ring: unknown }).ring));
                    }
                    toast.success('Ring updated', ring);
                },
                onError: (error) => {
                    setBusy('ring', false);
                    setRingOverride(previous);
                    const { title, description } = toggleErrorToast(error, "Couldn't set the ring");
                    toast.error(title, description);
                },
            });
        },
        [base.ring, base.vaultId, commitRing, setBusy, toast],
    );

    /** Shared optimistic-commit flow for both override actions. */
    const runOverride = useCallback(
        (config: {
            flagKey: string;
            optimistic: string | null;
            commit: Commit;
            variables: Record<string, unknown>;
            successTitle: string;
        }) => {
            const { flagKey, optimistic, commit, variables, successTitle } = config;
            const busyKey = `override:${flagKey}`;
            const previous = overridesRef.current[flagKey];
            const hadPrevious = flagKey in overridesRef.current;

            setBusy(busyKey, true);
            setOverride(flagKey, optimistic);

            const rollback = () =>
                setOverrides((current) => {
                    const next = { ...current };
                    if (hadPrevious) next[flagKey] = previous ?? null;
                    else delete next[flagKey];
                    return next;
                });

            commit({
                variables,
                onCompleted: (_response, errors) => {
                    setBusy(busyKey, false);
                    if (errors && errors.length > 0) {
                        rollback();
                        const { title, description } = toggleErrorToast(errors, "Couldn't update the override");
                        toast.error(title, description);
                        return;
                    }
                    toast.success(successTitle, flagKey);
                },
                onError: (error) => {
                    setBusy(busyKey, false);
                    rollback();
                    const { title, description } = toggleErrorToast(error, "Couldn't update the override");
                    toast.error(title, description);
                },
            });
        },
        [setBusy, setOverride, toast],
    );

    const onSetOverride = useCallback(
        (flagKey: string, state: string) =>
            runOverride({
                flagKey,
                optimistic: state,
                commit: commitSetOverride,
                variables: { key: flagKey, vaultId: base.vaultId, state },
                successTitle: state === 'ON' ? 'Override pinned on' : 'Override pinned off',
            }),
        [base.vaultId, commitSetOverride, runOverride],
    );

    const onClearOverride = useCallback(
        (flagKey: string) =>
            runOverride({
                flagKey,
                optimistic: null,
                commit: commitClearOverride,
                variables: { key: flagKey, vaultId: base.vaultId },
                successTitle: 'Override cleared',
            }),
        [base.vaultId, commitClearOverride, runOverride],
    );

    const vault = useMemo<VaultDetailView>(
        () => ({
            ...base,
            ring: ringOverride ?? base.ring,
            overrides: mergeOverrides(base.overrides, overrides),
        }),
        [base, ringOverride, overrides],
    );

    return { vault, busyKeys, onSetRing, onSetOverride, onClearOverride };
}
